import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useNavigate} from 'react-router-dom';
import SidebarLayout from 'layouts/SidebarLayout';
import {fetchStudentAnnualReport} from 'APIs/letrusAPI/studentAnnualReport';
import {useEffect, useState, useCallback} from 'react';
import {fetchCompositionCompetence} from 'APIs/letrusAPI/compositionCompetence';
import {InlineAlert} from 'letrus-ui';
import GradesEvolutionChart from './components/GradesEvolutionChart';
import SelectedFilter from './components/SelectedFilter';
import GeneralAnalysisCard from './components/GeneralAnalysisCard';
import styles from './EvolutionDashboard.module.scss';
import ActivitiesDetailsTable from './components/ActivitiesDetailsTable';

export type SelectedOptions = {
  grade: string;
  view: 'total' | 'competence';
  competence: number;
};

function EvolutionDashboard(): JSX.Element {
  const navigate = useNavigate();

  const [isFiltering, setIsFiltering] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({
    grade: 'ENEM',
    view: 'total',
    competence: 0,
  });

  const compositionCompetence = fetchCompositionCompetence();
  const annualReport = fetchStudentAnnualReport();
  const filteredAnnualReport = annualReport?.data;

  useEffect(() => {
    annualReport?.trigger({id: 1});
  }, []);

  const updateAnnualReportByFilter = useCallback(
    (options: SelectedOptions) => {
      setSelectedOptions(options);

      annualReport?.trigger({
        id: options.grade === 'ENEM' ? 1 : 61,
        ...(options.view === 'competence' && {
          competenceId: options.competence,
        }),
      });

      setIsFiltering(true);
    },
    [annualReport],
  );

  const selectedCompetence = compositionCompetence?.data?.results?.find(
    (competence) => competence.id === selectedOptions?.competence,
  );

  const selectedCompetenceInfo = selectedCompetence
    ? `${selectedCompetence.label} - ${selectedCompetence.nickname}`
    : null;

  return (
    <SidebarLayout>
      <main className={styles.container} role="main">
        <article className={styles.wrapper} aria-labelledby="dashboard-title">
          <header className={styles.header}>
            <nav aria-label="Navegação">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className={styles.goBackButton}
                aria-label="Voltar para a página anterior"
              >
                <FontAwesomeIcon
                  icon="chevron-left"
                  color="#FF4410"
                  size="2x"
                />
              </button>
            </nav>

            <div className={styles.titleContainer}>
              <h1 id="dashboard-title" className={styles.title}>
                Dashboard de Evolução
              </h1>

              <p className={styles.subtitle}>
                Acompanhe sua evolução de notas ao longo do tempo e identifique
                áreas de melhoria. Para visualizar, escolha a grade desejada,
                selecione se quer ver a nota total ou por competência e clique
                em &apos;Filtrar&apos;.
              </p>
            </div>
          </header>

          <SelectedFilter
            selectedOptions={selectedOptions}
            updateAnnualReportByFilter={updateAnnualReportByFilter}
            fetchOptionsCompetences={compositionCompetence?.trigger}
            compositionCompetence={compositionCompetence?.data?.results ?? []}
            isCompetenceLoading={compositionCompetence?.isMutating}
          />
        </article>

        {annualReport?.error || annualReport?.data?.activities.length === 0 ? (
          <>
            <InlineAlert
              backgroundColor="#ffeac4"
              mainColor="#f3af31"
              leftIcon={{icon: ['fad', 'circle-exclamation']}}
            >
              <article>
                <strong>Alerta </strong>
                <p>
                  No momento, não é possível exibir seus dados, pois você não
                  possui atividades finalizadas ou há notas pendentes de
                  sincronização. Para acessar o relatório, revise os filtros e
                  verifique se há atividades concluídas.
                </p>
              </article>
            </InlineAlert>
          </>
        ) : (
          <>
            <GeneralAnalysisCard
              annualReport={
                isFiltering
                  ? filteredAnnualReport ?? null
                  : annualReport?.data ?? null
              }
              view={selectedOptions.view}
              selectedCompetenceInfo={selectedCompetenceInfo ?? ''}
            />

            <GradesEvolutionChart
              activities={annualReport?.data?.activities ?? []}
              grade={selectedOptions.grade}
              view={selectedOptions.view}
              selectedCompetenceInfo={selectedCompetenceInfo ?? ''}
            />

            <ActivitiesDetailsTable
              activities={
                isFiltering
                  ? filteredAnnualReport?.activities ?? []
                  : annualReport?.data?.activities ?? []
              }
              view={selectedOptions.view}
              selectedCompetenceInfo={selectedCompetenceInfo ?? ''}
            />
          </>
        )}
      </main>
    </SidebarLayout>
  );
}

export default EvolutionDashboard;
